import type {
  Subscription,
  CheckoutSession,
  BillingPortalSession,
  Invoice,
  PaymentIntent,
} from "./subscription.entity";
import { zValidatedString, zValidatedPositiveNumber } from "@/lib/zod/helpers";
import { z } from "zod";

const SubscriptionSchema = z.object({
  id: zValidatedString("ID de suscripción"),
  end_date: zValidatedString("Fecha de fin"),
  cycle_type: zValidatedString("Tipo de ciclo"),
  plan: zValidatedString("Plan"),
  start_date: zValidatedString("Fecha de inicio"),
  status: zValidatedString("Estado de la suscripción"),
  stripe_subscription_id: zValidatedString("ID de suscripción de Stripe"),
  user_id: zValidatedString("ID del usuario"),
  stripe_customer_id: zValidatedString("ID de cliente de Stripe"),
  created_at: zValidatedString("Fecha de creación"),
  updated_at: zValidatedString("Fecha de actualización"),
});
SubscriptionSchema._output satisfies Subscription;

const InvoiceSchema = z.object({
  id: zValidatedString("ID de factura"),
  user_id: zValidatedString("ID de usuario"),
  user_stripe_email: zValidatedString("Email del usuario en Stripe"),
  subscription_id: zValidatedString("ID de la suscripción"),
  stripe_invoice_id: zValidatedString("ID de la factura en Stripe"),
  stripe_customer_id: zValidatedString("ID del cliente en stripe"),
  stripe_billing_reason: zValidatedString("Razón del cargo"),
  stripe_payment_intent_id: zValidatedString("ID del intento de cobro"),
  stripe_subscription_id: zValidatedString("ID de la suscripción en Stripe"),
  amount: zValidatedPositiveNumber("Monto"),
  currency: zValidatedString("Moneda"),
  status: zValidatedString("Estado"),
  attempt_count: zValidatedPositiveNumber("Número de intento"),
  next_payment_attempt: zValidatedString("Fecha del próximo intento"),
  due_date: zValidatedString("Fecha fin"),
  paid_date: zValidatedString("Fecha de pago"),
  invoice_url: zValidatedString("URL de la factura"),
  created_at: zValidatedString("Fecha de creación"),
  updated_at: zValidatedString("Fecha de actualización"),
});
InvoiceSchema._output satisfies Invoice;

const PaymentIntentSchema = z.object({
  id: zValidatedString("ID de pago"),
  user_id: zValidatedString("ID de usuario"),
  stripe_customer_id: zValidatedString("ID de cliente en stripe"),
  subscription_id: zValidatedString("ID de la suscripción"),
  stripe_subscription_id: zValidatedString("ID de la suscripción en Stripe"),
  stripe_payment_intent_id: zValidatedString("ID del intento de cobro"),
  stripe_invoice_id: zValidatedString("ID de la factura en Stripe"),
  stripe_created_at: zValidatedString("Fecha de creación en Stripe"),
  stripe_description: zValidatedString("Descripción en Stripe"),
  status: zValidatedString("Estado"),
  currency: zValidatedString("Moneda"),
  payment_method: zValidatedString("Método de pago"),
  failed_reason: zValidatedString("Razón del fallo"),
  failure_decline_code: zValidatedString("Código de rechazo"),
  created_at: zValidatedString("Fecha de creación"),
  updated_at: zValidatedString("Fecha de actualización"),
});
PaymentIntentSchema._output satisfies PaymentIntent;

const CheckoutSessionSchema = z.object({
  checkout_session_id: zValidatedString("Checkout session ID"),
  stripe_subscription_id: zValidatedString("Stripe subscription ID"),
  subscription_id: zValidatedString("Subscription ID"),
  url: zValidatedString("Checkout URL"),
  user_id: zValidatedString("User ID"),
});
CheckoutSessionSchema._output satisfies CheckoutSession;

const BillingPortalSessionSchema = z.object({
  billing_portal_session_id: zValidatedString("Billing portal session ID"),
  subscription_id: zValidatedString("Subscription ID"),
  stripe_customer_id: zValidatedString("Stripe customer ID"),
  user_id: zValidatedString("User ID"),
  url: zValidatedString("Billing portal URL"),
});
BillingPortalSessionSchema._output satisfies BillingPortalSession;

const SubscriptionListSchema = z.array(SubscriptionSchema);
SubscriptionListSchema._output satisfies Subscription[];

const InvoiceListSchema = z.array(InvoiceSchema);
InvoiceListSchema._output satisfies Invoice[];

const PaymentIntentListSchema = z.array(PaymentIntentSchema);
PaymentIntentListSchema._output satisfies PaymentIntent[];

export const SubscriptionSchemas = {
  Subscription: {
    Base: SubscriptionSchema,
    List: SubscriptionListSchema,
  },
  CheckoutSession: {
    Base: CheckoutSessionSchema,
  },
  BillingPortalSession: {
    Base: BillingPortalSessionSchema,
  },
  Invoice: {
    Base: InvoiceSchema,
    List: InvoiceListSchema,
  },
  PaymentIntent: {
    Base: PaymentIntentSchema,
    List: PaymentIntentListSchema,
  },
};
